import {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Page } from 'react-pdf';
import { KeyedPdfDocument } from '@/components/common/KeyedPdfDocument';
import { T, Var, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import {
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import * as Sentry from '@sentry/react';
import { cn } from '@/lib/utils';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';
// Sets pdfjs.GlobalWorkerOptions.workerSrc to the bundled worker asset.
import '@/lib/pdf-worker';

const MIN_SCALE = 0.5;
const MAX_SCALE = 5.0;
/**
 * Auto "fit to width" never magnifies past this. Manual zoom still goes to
 * MAX_SCALE. A container that is (or briefly measures) far wider than the page
 * — an unconstrained parent, a mid-layout measurement — would otherwise open
 * the document at "Fit 500%", which is never what fit-to-width means.
 */
const MAX_FIT_SCALE = 2.0;
const ZOOM_STEP = 0.1;
const RESIZE_DEBOUNCE_MS = 120;
const TRANSIENT_RETRY_DELAY_MS = 1_000;

function isTransientFetchError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return true;
  }
  const message = err instanceof Error ? err.message : String(err);
  return (
    message.includes('Failed to fetch') ||
    message.includes('NetworkError') ||
    message.includes('network error')
  );
}

interface PDFPageLike {
  pageNumber: number;
  getViewport: (params: { scale: number }) => { width: number };
}

interface PDFDocumentLike {
  numPages: number;
}

export interface PDFViewerProps {
  url: string;
  /**
   * Called when the signed URL returns a 403 (expired).
   * The parent should trigger a data refetch so a fresh URL flows in via props.
   * When omitted, 403 errors fall through to the normal error UI immediately.
   */
  onUrlExpired?: () => void;
  width?: number; // fallback base page width for backward compatibility
  pageMode?: 'single' | 'all-pages';
  className?: string;
  stopClickPropagation?: boolean;
}

function clampScale(value: number): number {
  return Math.min(Math.max(value, MIN_SCALE), MAX_SCALE);
}

function roundScale(value: number): number {
  return Math.round(value * 10) / 10;
}

function measureContentWidth(element: HTMLDivElement): number {
  const styles = window.getComputedStyle(element);
  const horizontalPadding =
    Number.parseFloat(styles.paddingLeft) +
    Number.parseFloat(styles.paddingRight);

  return Math.max(Math.round(element.clientWidth - horizontalPadding), 1);
}

const documentLoadingFallback = (
  <div className="flex justify-center items-center min-h-[400px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const pageLoadingFallback = (
  <div className="flex justify-center items-center min-h-[400px]">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
  </div>
);

export function PDFViewer({
  url,
  onUrlExpired,
  width = 500,
  pageMode = 'single',
  className,
  stopClickPropagation = true,
}: PDFViewerProps) {
  return (
    <div
      className={cn('flex h-full min-h-0 flex-col', className)}
      onClick={
        stopClickPropagation ? (event) => event.stopPropagation() : undefined
      }
    >
      <PDFViewerContent
        url={url}
        width={width}
        pageMode={pageMode}
        onUrlExpired={onUrlExpired}
      />
    </div>
  );
}

interface PDFViewerContentProps {
  url: string;
  width: number;
  pageMode: 'single' | 'all-pages';
  onUrlExpired?: () => void;
}

interface PDFDocumentRendererProps {
  currentPageNumber: number;
  numPages: number | null;
  onDocumentLoadError: (err: unknown) => void;
  onDocumentLoadSuccess: (pdf: PDFDocumentLike) => void;
  onPageLoadSuccess: (page: PDFPageLike) => void;
  onRetry: () => void;
  pageMode: 'single' | 'all-pages';
  renderWidth: number;
  url: string;
  retryKey: number;
}

const PDFDocumentRenderer = memo(function PDFDocumentRenderer({
  currentPageNumber,
  numPages,
  onDocumentLoadError,
  onDocumentLoadSuccess,
  onPageLoadSuccess,
  onRetry,
  pageMode,
  renderWidth,
  url,
  retryKey,
}: PDFDocumentRendererProps) {
  return (
    <KeyedPdfDocument
      url={url}
      retryKey={retryKey}
      onLoadSuccess={onDocumentLoadSuccess}
      loading={documentLoadingFallback}
      onLoadError={onDocumentLoadError}
      error={
        <div className="text-center py-8 space-y-2">
          <p className="text-destructive">
            <T>Error loading PDF preview.</T>
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" onClick={onRetry}>
              <T>Try again</T>
            </Button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary underline hover:text-primary/80"
            >
              <T>Open in new tab</T>
            </a>
          </div>
        </div>
      }
    >
      {pageMode === 'all-pages' && numPages ? (
        <div className="flex flex-col items-center gap-4">
          {Array.from({ length: numPages }, (_, index) => (
            <Page
              key={index + 1}
              pageNumber={index + 1}
              onLoadSuccess={onPageLoadSuccess}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              width={renderWidth}
              loading={pageLoadingFallback}
            />
          ))}
        </div>
      ) : (
        <div className="flex justify-center">
          <Page
            pageNumber={currentPageNumber}
            onLoadSuccess={onPageLoadSuccess}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            width={renderWidth}
            loading={pageLoadingFallback}
          />
        </div>
      )}
    </KeyedPdfDocument>
  );
});

function PDFViewerContent({
  url,
  width,
  pageMode,
  onUrlExpired,
}: PDFViewerContentProps) {
  const gt = useGT();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeTimeoutRef = useRef<number | null>(null);
  const lastMeasuredWidthRef = useRef<number | null>(null);
  const latestObservedWidthRef = useRef<number | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfPageWidth, setPdfPageWidth] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [zoomMode, setZoomMode] = useState<'auto' | 'manual'>('auto');
  const [manualScale, setManualScale] = useState(1);

  // URL expiry handling: track which URL we already retried to prevent loops
  const [urlExpired, setUrlExpired] = useState(false);
  const retriedUrlRef = useRef<string | null>(null);
  const prevUrlRef = useRef(url);
  const refreshTimerRef = useRef<number | null>(null);

  // Transient fetch error: allow one retry per URL after a short delay
  const [retryCount, setRetryCount] = useState(0);
  const transientRetriedUrlRef = useRef<string | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const isOnline = useConnectionStatus();

  // When url prop changes (parent refetched), reset expired state inline (no useEffect)
  if (prevUrlRef.current !== url) {
    prevUrlRef.current = url;
    if (urlExpired) {
      setUrlExpired(false);
    }
    if (refreshTimerRef.current !== null) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    transientRetriedUrlRef.current = null;
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (retryCount !== 0) {
      setRetryCount(0);
    }
  }

  // Clear timers on unmount to prevent state updates after unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current !== null) {
        window.clearTimeout(refreshTimerRef.current);
      }
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  const basePageWidth = pdfPageWidth ?? width;
  const fitScale =
    !containerWidth || !basePageWidth
      ? 1
      : Math.min(clampScale(containerWidth / basePageWidth), MAX_FIT_SCALE);

  const effectiveScale = zoomMode === 'auto' ? fitScale : manualScale;
  const documentRenderWidth = Math.round(basePageWidth * effectiveScale);
  const currentPageNumber = numPages
    ? Math.min(pageNumber, numPages)
    : pageNumber;

  const onDocumentLoadSuccess = useCallback((pdf: PDFDocumentLike) => {
    setNumPages(pdf.numPages);
  }, []);

  const onPageLoadSuccess = useCallback((page: PDFPageLike) => {
    if (page.pageNumber !== 1) {
      return;
    }

    setPdfPageWidth(page.getViewport({ scale: 1 }).width);
  }, []);

  const onDocumentLoadError = useCallback(
    (err: unknown) => {
      const message = err instanceof Error ? err.message : String(err);
      const isSignedUrlExpiry =
        message.includes('403') || message.includes('AccessDenied');

      if (isSignedUrlExpiry && onUrlExpired && retriedUrlRef.current !== url) {
        retriedUrlRef.current = url;
        setUrlExpired(true);
        onUrlExpired();

        // Fall back to error UI if parent doesn't provide a fresh URL within 15s
        refreshTimerRef.current = window.setTimeout(() => {
          refreshTimerRef.current = null;
          setUrlExpired(false);
        }, 15_000);
        return;
      }

      if (
        isTransientFetchError(err) &&
        transientRetriedUrlRef.current !== url
      ) {
        // First transient failure for this URL.
        // Offline: retry won't help and this isn't a real app error — swallow.
        if (!isOnline) {
          return;
        }
        transientRetriedUrlRef.current = url;
        retryTimerRef.current = window.setTimeout(() => {
          retryTimerRef.current = null;
          setRetryCount((count) => count + 1);
        }, TRANSIENT_RETRY_DELAY_MS);
        return;
      }

      Sentry.captureException(err);
      console.error(err);
    },
    [url, onUrlExpired, isOnline],
  );

  const commitContainerWidth = useCallback((nextWidth: number) => {
    if (lastMeasuredWidthRef.current === nextWidth) {
      return;
    }

    lastMeasuredWidthRef.current = nextWidth;
    startTransition(() => {
      setContainerWidth(nextWidth);
    });
  }, []);

  const setMeasuredContainer = useCallback(
    (element: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      if (resizeTimeoutRef.current !== null) {
        window.clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }

      if (!element) {
        return;
      }

      const initialWidth = measureContentWidth(element);
      latestObservedWidthRef.current = initialWidth;
      commitContainerWidth(initialWidth);

      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }

        const nextWidth = Math.max(Math.round(entry.contentRect.width), 1);
        latestObservedWidthRef.current = nextWidth;

        if (zoomMode === 'manual') {
          return;
        }

        if (resizeTimeoutRef.current !== null) {
          window.clearTimeout(resizeTimeoutRef.current);
        }

        resizeTimeoutRef.current = window.setTimeout(() => {
          resizeTimeoutRef.current = null;
          commitContainerWidth(nextWidth);
        }, RESIZE_DEBOUNCE_MS);
      });

      observer.observe(element);
      resizeObserverRef.current = observer;
    },
    [commitContainerWidth, zoomMode],
  );

  const handleManualRetry = useCallback(() => {
    transientRetriedUrlRef.current = null;
    setRetryCount((count) => count + 1);
  }, []);

  const flushAutoWidth = useCallback(() => {
    const nextWidth = latestObservedWidthRef.current;
    if (nextWidth === null) {
      return;
    }

    if (resizeTimeoutRef.current !== null) {
      window.clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = null;
    }

    commitContainerWidth(nextWidth);
  }, [commitContainerWidth]);

  const changeZoom = (delta: number) => {
    setZoomMode('manual');
    setManualScale((prev) => {
      const start = zoomMode === 'auto' ? effectiveScale : prev;
      return clampScale(roundScale(start + delta));
    });
  };

  const zoomIn = () => changeZoom(ZOOM_STEP);
  const zoomOut = () => changeZoom(-ZOOM_STEP);
  const resetToFit = () => {
    flushAutoWidth();
    setZoomMode('auto');
  };
  const showPageNavigation = pageMode === 'single';

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b p-2">
        {showPageNavigation ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPageNumber(Math.max(1, currentPageNumber - 1))}
              disabled={currentPageNumber <= 1}
              aria-label={gt('Previous page')}
            >
              <CaretLeftIcon className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              <T>
                Page <Var>{currentPageNumber}</Var> of{' '}
                <Var>{numPages || '?'}</Var>
              </T>
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setPageNumber(Math.min(numPages || 1, currentPageNumber + 1))
              }
              disabled={!numPages || currentPageNumber >= numPages}
              aria-label={gt('Next page')}
            >
              <CaretRightIcon className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">
            <T>
              Pages: <Var>{numPages || '?'}</Var>
            </T>
          </span>
        )}

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={zoomOut}
            disabled={effectiveScale <= MIN_SCALE}
            aria-label={gt('Zoom out')}
          >
            <MagnifyingGlassMinusIcon className="h-4 w-4" />
          </Button>
          <span className="min-w-[4.5rem] text-center text-sm text-muted-foreground">
            {zoomMode === 'auto'
              ? gt('Fit {percentage}%', {
                  percentage: String(Math.round(effectiveScale * 100)),
                })
              : gt('{percentage}%', {
                  percentage: String(Math.round(effectiveScale * 100)),
                })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={zoomIn}
            disabled={effectiveScale >= MAX_SCALE}
            aria-label={gt('Zoom in')}
          >
            <MagnifyingGlassPlusIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={resetToFit}
            disabled={zoomMode === 'auto'}
            aria-label={gt('Fit to width')}
          >
            <T>Fit</T>
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <div ref={setMeasuredContainer} className="min-h-full p-4">
          {urlExpired ? (
            <div className="flex flex-col justify-center items-center min-h-[400px] gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <p className="text-sm text-muted-foreground">
                <T>Refreshing document...</T>
              </p>
            </div>
          ) : (
            <PDFDocumentRenderer
              currentPageNumber={currentPageNumber}
              numPages={numPages}
              onDocumentLoadError={onDocumentLoadError}
              onDocumentLoadSuccess={onDocumentLoadSuccess}
              onPageLoadSuccess={onPageLoadSuccess}
              onRetry={handleManualRetry}
              pageMode={pageMode}
              renderWidth={documentRenderWidth}
              url={url}
              retryKey={retryCount}
            />
          )}
        </div>
      </div>
    </>
  );
}
